import type { Card } from '@adyen/adyen-web';
import type { FormikErrors } from 'formik';
import { Form, Formik } from 'formik';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import z, { ZodError } from 'zod';

import { BANNERS, logSentryError, SentryAcpCreditCardsErrors as SentryErrors } from '@libs/common-next';
import { CARD_HOLDER_NAME_PATTERN } from '@libs/core';

import type { CardState } from '../../hooks/types';
import type { AddressFormType } from '../AddressForm/Address.schema';
import { AdyenForm } from '../AdyenForm';
import { BillingAddress } from '../BillingAddress';
import type { ExperianResponseLayout, FormProps } from '../ExperianModal';
import { ExperianModal, validateExperianAddressLayout } from '../ExperianModal';
import { IsDefaultCheckbox } from './IsDefaultCheckbox';
import type { AddPaymentFormInterface } from './schema';
import { paymentFormSchema } from './schema';
import type { PaymentAddressForOrderLink } from './types';
import { addPayment, isPaymentFormAddressInMarketCountry, validateAddressForm } from './utils';
import { nameOnCardInvalidMsg } from './validationErrors';

const DEFAULT_ADDRESS_FORM_STATE: AddressFormType = {
    apt: '',
    city: '',
    state: '',
    streetAddress: '',
    zipCode: '',
};

function getDefaultPaymentFormState(isPrimary = false): AddPaymentFormInterface {
    return {
        address: DEFAULT_ADDRESS_FORM_STATE,
        addressType: 'form',
        firstName: '',
        lastName: '',
        isPrimary,
        phone: '',
    };
}

export type AddPaymentForm = {
    onCloseForm: () => void;
    onSuccess?: ({
        isDefault,
        addressToLink,
    }: {
        isDefault?: boolean;
        addressToLink?: PaymentAddressForOrderLink;
    }) => Promise<void>;
    onError?: (error: unknown) => void;
    onExperianModalVisibilityChange?: (isOpen: boolean) => void;
    defaultAddressId?: string;
    isDefaultCheckChecked: boolean;
    isDefaultCheckDisabled: boolean;
    setBillingAddressAsDefaultShippingInMarketOnly?: boolean;
    addAddressIconSize?: 'sm' | 'md' | 'lg';
    onClearError?: () => void;
    onAddressValidationApiError?: () => void;
    hideCancelCta?: boolean;
    submitCtaText?: string;
};

export function AddPaymentForm({
    onCloseForm,
    onSuccess,
    onError,
    onExperianModalVisibilityChange = () => {},
    defaultAddressId,
    isDefaultCheckChecked,
    isDefaultCheckDisabled,
    setBillingAddressAsDefaultShippingInMarketOnly = false,
    addAddressIconSize,
    onClearError,
    onAddressValidationApiError,
    hideCancelCta,
    submitCtaText,
}: Readonly<AddPaymentForm>): ReactElement {
    const [adyenForm, setAdyenForm] = useState<Card>();
    const [adyenFormState, setAdyenFormState] = useState<CardState>();
    const [holderNameError, setHolderNameError] = useState<string | undefined>();
    const [isExperianLoading, setIsExperianLoading] = useState(false);
    const [experianResponse, setExperianResponse] = useState<ExperianResponseLayout | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const defaultPayment = getDefaultPaymentFormState(isDefaultCheckChecked);
    const paymentFormState: AddPaymentFormInterface = defaultAddressId
        ? {
              ...defaultPayment,
              address: undefined,
              addressType: 'id',
              firstName: undefined,
              lastName: undefined,
              isPrimary: isDefaultCheckChecked,
              phone: undefined,
              selectedAddressId: defaultAddressId.toString(),
          }
        : defaultPayment;

    const validate = async (values: AddPaymentFormInterface) => {
        let errors: FormikErrors<AddPaymentFormInterface> = {};

        try {
            paymentFormSchema.parse(values);
        } catch (error) {
            if (error instanceof ZodError) {
                errors = z.flattenError(error).fieldErrors;
            }
        }

        if (values.address) {
            const addressErrors = await validateAddressForm(values.address);

            if (addressErrors) {
                errors.address = addressErrors as FormikErrors<AddPaymentFormInterface> as string;
            }
        }

        return Object.keys(errors) ? errors : true;
    };

    useEffect(() => {
        const holderName = adyenFormState?.data?.paymentMethod?.holderName ?? '';
        const hasInvalidChars = !!holderName && !CARD_HOLDER_NAME_PATTERN.test(holderName);

        setHolderNameError(hasInvalidChars ? nameOnCardInvalidMsg : undefined);
    }, [adyenFormState]);

    const validateAdyenForm = (showValidationUI = true): boolean => {
        const holderName = adyenFormState?.data?.paymentMethod?.holderName ?? '';
        const hasInvalidChars = !!holderName && !CARD_HOLDER_NAME_PATTERN.test(holderName);
        const isAdyenInvalid = adyenFormState?.isValid === false || hasInvalidChars;

        if (isAdyenInvalid) {
            if (showValidationUI) {
                adyenForm?.showValidation();
                onError?.(BANNERS.invalidPaymentMethodAdyen.text ?? '');
            }
            return false;
        }

        return true;
    };

    const handleAddressError: (errors?: FormikErrors<AddPaymentFormInterface>) => boolean = errors => {
        const hasFormErrors = errors ? Object.keys(errors).length > 0 : false;

        if (hasFormErrors) {
            onError?.(BANNERS.invalidShippingAddress.text ?? '');
            return false;
        }

        onClearError?.();
        return validateAdyenForm(true);
    };

    const handleSubmitForm = async (values: AddPaymentFormInterface): Promise<void> => {
        if (!validateAdyenForm(true) || !adyenFormState) {
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await addPayment(adyenFormState, values, {
                setBillingAddressAsDefaultShippingInMarketOnly,
            });

            if (response && 'cardId' in response) {
                const addressToLink =
                    setBillingAddressAsDefaultShippingInMarketOnly &&
                    values.addressType === 'form' &&
                    isPaymentFormAddressInMarketCountry(values)
                        ? {
                              firstName: values.firstName,
                              lastName: values.lastName,
                              phone: values.phone,
                              address: values.address,
                          }
                        : undefined;

                await onSuccess?.({ isDefault: response.isPrimary, addressToLink });
            } else {
                throw new Error('Failed to add payment method');
            }
        } catch (error) {
            logSentryError(error, SentryErrors.AddPaymentForm.SubmitFailed);

            onError?.(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const validateExperianOnSubmit = async (values: AddPaymentFormInterface) => {
        onClearError?.();

        if (!validateAdyenForm(true)) {
            return;
        }
        if (values.addressType === 'id') {
            await handleSubmitForm(values);
        } else {
            const addressValues = values.address;
            setIsExperianLoading(true);
            const experianResponseValue = await validateExperianAddressLayout(addressValues);

            setIsExperianLoading(false);

            if (!experianResponseValue) {
                onAddressValidationApiError?.();
                return;
            }

            setExperianResponse(experianResponseValue);
        }
    };

    return (
        <div>
            <Formik
                initialValues={paymentFormState}
                validate={validate}
                validateOnChange={true}
                onSubmit={validateExperianOnSubmit}
            >
                {formikProps => (
                    <Form noValidate onSubmit={formikProps.handleSubmit} data-testid="add-billing-address-form">
                        <AdyenForm
                            setAdyenForm={setAdyenForm}
                            setAdyenFormState={setAdyenFormState}
                            holderNameError={holderNameError}
                        />
                        <ExperianModal
                            experianResponse={experianResponse}
                            saveSelectedAddress={async (formProps: FormProps) => {
                                await handleSubmitForm(formProps as AddPaymentFormInterface);
                            }}
                            loading={isExperianLoading}
                            setIsLoading={setIsExperianLoading}
                            onVisibilityChange={onExperianModalVisibilityChange}
                        />

                        <IsDefaultCheckbox disabled={isDefaultCheckDisabled} />

                        <BillingAddress
                            onCancel={onCloseForm}
                            defaultAddressId={defaultAddressId}
                            isLoading={isExperianLoading || isSubmitting}
                            handleErrorBanner={handleAddressError}
                            addAddressIconSize={addAddressIconSize}
                            hideCancelCta={hideCancelCta}
                            submitCtaText={submitCtaText}
                        />
                    </Form>
                )}
            </Formik>
        </div>
    );
}
